<template>
  <div class="ima-sign-editor">

    <div class="ima-sign-editor__container">
      <div class="ima-sign-editor__workspace">

        <ima-sign-editor-toolbar>

          <button v-if="canEditSigns" class="button is-success" type="button" @click="onTransfer()" :disabled="!canTransferSign || sign.isSaving">
            <span class="icon">
              <b-icon icon="forward"></b-icon>
            </span>
            <span>Weitergabe</span>
          </button>

          <button v-if="canPublishVariants" class="button is-info" :class="{'is-loading' : variant.isPublishing}" type="button" @click="onPublish()" :disabled="!signIsFinished || variant.data.isPublished || sign.isSaving">
            <span class="icon">
              <b-icon icon="publish"></b-icon>
            </span>
            <span>Veröffentlichen</span>
          </button>

          <button v-if="canRevokeVariants" class="button is-danger" :class="{'is-loading' : variant.isRevoking}" type="button" @click="onRevoke()" :disabled="!variant.data.isPublished || sign.isSaving">
            <span class="icon">
              <b-icon icon="settings_backup_restore"></b-icon>
            </span>
            <span>Zurückziehen</span>
          </button>

          <hr />

          <div v-if="variant.data.isPublished && canViewFinalPDF" class="field has-addons">
            <div class="control fullwidth">
              <input class="input is-small fullwidth" type="text" :value="downloadURI" readonly />
            </div>
            <div class="control ">
              <b-tooltip label="URL kopieren" position="is-right">
                <button v-clipboard="downloadURI" class="button is-small">
                  <b-icon icon="content_copy" size="is-small"></b-icon>
                </button>
              </b-tooltip>
            </div>
          </div>

          <a v-if="variant.data.isPublished && canViewFinalPDF" class="button is-primary is-inverted" :href="downloadURI" download>
            <span class="icon">
              <b-icon icon="file_download" size="is-small"></b-icon>
            </span>
            <span>Download</span>
          </a>

          <a class="button is-primary is-inverted" :href="previewURI" target="_blank">
            <span class="icon">
              <b-icon icon="pageview" size="is-small"></b-icon>
            </span>
            <span>Vorschau</span>
          </a>

          <button class="button is-primary is-inverted" :disabled="!signIsFinished" @click="onAddSignToShoppingCart()">
            <span class="icon">
              <b-icon icon="add_shopping_cart" size="is-small"></b-icon>
            </span>
            <span>In den Warenkorb</span>
          </button>

          <hr />

          <button v-if="canEditSigns" class="button is-primary is-inverted" type="button" @click="onVariantRename()" :disabled="!signIsEditable">
            <span class="icon">
              <b-icon icon="title" size="is-small"></b-icon>
            </span>
            <span>Umbenennen</span>
          </button>

          <button v-if="canEditSigns" type="button" :disabled="!signIsEditable" class="button is-primary is-inverted" @click="onVariantAdd()">
            <span class="icon">
              <b-icon icon="content_copy" size="is-small"></b-icon>
            </span>
            <span>Kopieren</span>
          </button>

          <button v-if="canEditSigns" type="button" :disabled="variant.data.isDefault || !signIsEditable" class="button is-primary is-inverted" @click="onVariantDefineAsDefault()">
            <span class="icon">
              <b-icon icon="flag" size="is-small"></b-icon>
            </span>
            <span>Als Standard festlegen</span>
          </button>

          <button v-if="canEditSigns" type="button" :disabled="variants.data.length<=1  || !signIsEditable" class="button is-danger" @click="onVariantRemove()">
            <span class="icon">
              <b-icon icon="delete" size="is-small"></b-icon>
            </span>
            <span>Löschen</span>
          </button>

        </ima-sign-editor-toolbar>

        <div class="ima-sign-editor__stage" ref="stage">
          <ima-sign-svg class="ima-sign-editor__svg" :class="{'sticky' : isViewportHigherThanSign}" ref="sign"/>
          <div class="ima-sign-editor__placeholder" ref="placeholder" :style="{ width: `${svg.width}px`, height: `${svg.height}px`}"></div>
        </div>

        <ima-sign-editor-panel v-if="signIsEditable && canEditSigns" />

      </div>

    </div>

  </div>
</template>


<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import { transferSign } from '@/store/modules/sign'
import { saveLabels } from '@/store/modules/labels'
import {
  setDefaultVariant,
  renameVariant,
  publishVariant,
  revokeVariant
} from '@/store/modules/variant'
import { removeVariant } from '@/store/modules/variants'
import {
  openVariantModal,
  openAddSignToShoppingCartModal,
  openTransferModal
} from '@/store/modules/modals'
import { selectSign } from '@/store/modules/shopping-cart'
import {
  canEditSigns,
  canTransferSign,
  getTransferOptions,
  signIsEditable,
  canPublishVariants,
  canRevokeVariants,
  canViewFinalPDF
} from '@/services/permissions'

import SignEditorPanel from '@/components/SignEditorPanel'
import SignSVG from '@/components/SignSVG'
import SignEditorToolbar from '@/components/SignEditorToolbar'

export default {
  name: 'ima-sign-editor',
  components: {
    'ima-sign-editor-panel': SignEditorPanel,
    'ima-sign-editor-toolbar': SignEditorToolbar,
    'ima-sign-svg': SignSVG
  },
  data () {
    return {
      isUpdatingSignPosition: false,
      initialPosition: null
    }
  },
  computed: {
    ...mapState(['sign', 'labels', 'variant', 'variants', 'svg']),
    signIsFinished () {
      return this.sign.data.status.name === 'ABGESCHLOSSEN'
    },
    signIsEditable () {
      return signIsEditable()
    },
    canEditSigns () {
      return canEditSigns()
    },
    canPublishVariants () {
      return canPublishVariants()
    },
    canRevokeVariants () {
      return canRevokeVariants()
    },
    canTransferSign () {
      return canTransferSign()
    },
    canViewFinalPDF () {
      return canViewFinalPDF()
    },
    previewURI () {
      if (!this.sign.data || !this.variant.data) {
        return null
      }
      return `${SERVER_URL}/pdf/${this.sign.data.id}/${this.variant.data.id}`
    },
    downloadURI () {
      if (!this.sign.data || !this.variant.data) {
        return null
      }
      return `${window.location.protocol}${SERVER_URL}/download/${this.variant
        .data.filePath}`
    },
    isViewportHigherThanSign () {
      if (!this.svg || this.svg.height <= 0) {
        return false
      }
      return this.svg && this.svg.height < window.innerHeight
    }
  },
  methods: {
    ...mapMutations([
      openVariantModal,
      selectSign,
      openAddSignToShoppingCartModal,
      openTransferModal
    ]),
    ...mapActions([
      saveLabels,
      removeVariant,
      renameVariant,
      setDefaultVariant,
      transferSign,
      publishVariant,
      revokeVariant
    ]),
    onAddSignToShoppingCart () {
      this.selectSign(this.sign.data)
      this.openAddSignToShoppingCartModal()
    },
    onTransfer () {
      const options = getTransferOptions()
      // No need for decisions if there is only one option
      if (options.length === 1) {
        this.transferSign(options[0])
      } else {
        this.openTransferModal()
      }
    },
    onPublish () {
      this.publishVariant({ sign: this.sign.data, variant: this.variant.data })
    },
    onRevoke () {
      this.revokeVariant({ sign: this.sign.data, variant: this.variant.data })
    },
    onVariantAdd () {
      this.openVariantModal()
    },
    onVariantDefineAsDefault () {
      this.setDefaultVariant()
    },
    onVariantRename () {
      this.$dialog.prompt({
        message: 'Neuer Titel der Variante',
        inputMaxlength: 256,
        inputPlaceholder: this.variant.data.name,
        confirmText: 'Umbenennen',
        cancelText: 'Abbrechen',
        onConfirm: name => {
          this.renameVariant(name)
        }
      })
    },
    onVariantRemove () {
      if (this.variant.data.isDefault) {
        this.$dialog.alert({
          title: 'Löschen nicht möglich',
          message:
            'Die aktuelle Variante ist als <strong>Standard definiert</strong> und kann somit nicht gelöscht werden.',
          confirmText: 'OK'
        })
      } else {
        this.$dialog.confirm({
          title: 'Variante löschen',
          message: `Sind Sie sicher dass Sie die Variante <strong> ${this
            .variant.data
            .name}</strong> unwiderruflich <strong>löschen</strong> wollen ? `,
          confirmText: 'Variante löschen',
          cancelText: 'Abbrechen',
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => {
            this.removeVariant(this.variant.data)
          }
        })
      }
    },
    onScroll () {
      requestAnimationFrame(this.updateSignPosition.bind(this))
    },
    updateSignPosition () {
      if (
        !this.isViewportHigherThanSign ||
        this.isUpdatingSignPosition ||
        !this.$refs ||
        !this.$refs.sign
      ) {
        return
      }
      this.isUpdatingSignPosition = true

      if (!this.initialPosition) {
        this.initialPosition = this.$refs.placeholder.getBoundingClientRect()
      }
      const sign = this.$refs.sign.$el
      const currentPosition = this.$refs.placeholder.getBoundingClientRect()
      const currentContainer = this.$refs.stage.getBoundingClientRect()

      const lowerLimit = currentContainer.height

      sign.style.left = `${currentPosition.left}px`

      if (
        window.scrollY > this.initialPosition.top &&
        window.scrollY < lowerLimit
      ) {
        sign.style.top = `0px`
      } else {
        sign.style.top = `${currentPosition.top}px`
      }
      this.isUpdatingSignPosition = false
    }
  },
  created () {
    this.onScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScroll)
    window.addEventListener('resize', this.onScroll)
    this.onScroll()
  },
  updated () {
    this.onScroll()
  },
  destroyed () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
  }
}
</script>


<style lang="scss">
@import '../scss/settings';

.ima-sign-editor {
  &__container {
    border: 1px solid #dbdbdb;
    overflow: hidden;
    padding: 1rem;
  }

  &__workspace {
    align-content: stretch;
    align-items: stretch;
    display: flex;
    position: relative;
    width: 100%;
  }

  &__stage {
    display: block;
    margin: 0 auto;
    position: relative;
    width: fit-content;
  }

  &__svg {
    border: 1px solid #dbdbdb;
    position: absolute;
    left: 0;
    top: 0;
    width: fit-content;

    &.sticky {
      position: fixed;
    }
  }

  &__placeholder {
    display: block;
  }

  &__settings {
    padding: 0.5rem 1rem;
    position: absolute;
    right: 1rem;
    top: 1rem;
    max-width: 30rem;
    min-width: 20rem;

    .b-tabs {
      .tab-content {
        background-color: $secondary;
        border: 1px solid darken($secondary, 10);
        border-top: 0;
        padding: 0.5rem 1rem;
        overflow: auto;
        max-height: 70vh;
      }
    }
    .tabs {
      margin-bottom: 0;

      li {
        a {
          border-bottom: 0;
          padding: 0.2rem 0.5rem;
        }
        &.is-active {
          a {
            background-color: darken($secondary, 50);
          }
        }
      }
    }
  }

  &__label {
    margin-bottom: 0.8rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SignEditor.vue